import { useMemo, useCallback } from 'react';
import { pick, compareObjects } from 'js/utils/ramda.util';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useValidate } from './useValidate';
import { useExportControl } from './useExportControl';
import { useOnFilterChange } from './useOnFilterChange';
import { useOnProductChange } from './useOnProductChange';
import { useOnGamesSourceChange } from './useOnGamesSourceChange';
import { useGetEnabledProducts } from './useGetEnabledProducts';
import { PERIODS, ANOTHER_PERIOD_VALUE, GAMES_SOURCES } from '../constants';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../store';

export const useTableHeader = () => {
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const {
        period,
        endDate,
        product,
        startDate,
        isLoading,
        gamesSource,
        isExportLoading
    } = useDynamicReducerSelector(pick([
        'period',
        'endDate',
        'product',
        'startDate',
        'isLoading',
        'gamesSource',
        'isExportLoading'
    ]));
    const isComponentLoading = isLoading || isExportLoading;

    const headerInitialValues = useMemo(() => ({
        product,
        gamesSource,
        period,
        from: startDate,
        until: endDate
    }), [product, gamesSource, period, startDate, endDate]);

    const headerValidate = useValidate();

    const getIsDisabled = useCallback(values => {
        const startDateToCompare = helpers.intoDate(values.from);
        const endDateToCompare = helpers.intoDate(values.until);

        return isComponentLoading
        || compareObjects(startDate, startDateToCompare)
        && compareObjects(endDate, endDateToCompare)
        || compareObjects(helpers.startOfDay(startDate), startDateToCompare)
        && compareObjects(helpers.startOfDay(endDate), endDateToCompare);
    }, [startDate, endDate, isComponentLoading]);

    const anotherPeriodFilter = useMemo(() => {
        return period !== ANOTHER_PERIOD_VALUE ? null : {
            from: { label: translate('filter.from'), disabled: isComponentLoading },
            until: { label: translate('filter.until'), disabled: isComponentLoading },
            submit: {
                title: translate('filter.btn.apply'),
                getIsDisabled
            }
        };
    }, [translate, getIsDisabled, period, isComponentLoading]);

    const onAnotherPeriodApply = useCallback((values) => {
        dispatch(actions.setData({
            startDate: helpers.startOfDay(values.from),
            endDate: helpers.endOfDay(values.until),
            page: 1
        }));
    }, [dispatch]);

    const { anotherPeriodEnabled } = useComponentConfig();

    const periodOptions = useMemo(() => [
        ...PERIODS.map((item) => ({
            ...item,
            label: translate(item.label)
        })),
        ...anotherPeriodEnabled && [
            { label: translate('filter.periods.anotherPeriod'), value: ANOTHER_PERIOD_VALUE }
        ] || []
    ], [translate, anotherPeriodEnabled]);

    const getEnabledProducts = useGetEnabledProducts();

    const productOptions = useMemo(() =>
        getEnabledProducts()
            .map((value) => ({
                    label: translate(`products.${value}`), value
                })
            ), [translate, getEnabledProducts]);

    const gamesSourceOptions = useMemo(() =>
        Object.values(GAMES_SOURCES)
            .map((value) => ({
                    label: translate(`gamesSource.${value}`), value
                })
            ), [translate]);

    const onFilterChange = useOnFilterChange();
    const onProductChange = useOnProductChange();
    const afterFiltersControls = useExportControl();
    const onGamesSourceChange = useOnGamesSourceChange();

    return useMemo(() => ({
        filters: [{
            id: 0,
            name: 'period',
            options: periodOptions,
            onChange: onFilterChange,
            label: translate('filter.title.period'),
            disabled: isComponentLoading || product === 'SPORTSBOOK' && gamesSource === GAMES_SOURCES.OPEN_GAMES,
            'aria-label': translate(['filter.title.period', 'playtech.mobile.system.select.aria-label'])
        },
        ...productOptions.length > 1 ? [{
            id: 1,
            name: 'product',
            options: productOptions,
            onChange: onProductChange,
            disabled: isComponentLoading,
            label: translate('filter.title.product'),
            'aria-label': translate(['filter.title.product', 'playtech.mobile.system.select.aria-label'])
        }] : [],
        ...product === 'SPORTSBOOK' ? [{
            id: 2,
            name: 'gamesSource',
            options: gamesSourceOptions,
            disabled: isComponentLoading,
            onChange: onGamesSourceChange,
            label: translate('filter.title.gamesSource'),
            'aria-label': translate(['filter.title.gamesSource', 'playtech.mobile.system.select.aria-label'])
        }] : []],
        headerValidate,
        headerInitialValues,
        anotherPeriodFilter,
        onAnotherPeriodApply,
        afterFiltersControls
    }), [
        product, translate, gamesSource, periodOptions, onFilterChange, headerValidate,
        productOptions, onProductChange, isComponentLoading, gamesSourceOptions, headerInitialValues,
        anotherPeriodFilter, onGamesSourceChange, onAnotherPeriodApply, afterFiltersControls
    ]);
};
